import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import { DomUtils } from "@tento-ui/dom";
import { LazySync } from "@tento-ui/lazy";
import { EventItemPresentation } from "@tento-lona";
import { EventItem } from "@tento-lona/sheets";
import { Point } from "@tento-core/point";
import type { Inset } from "@tento-core/inset";

/** The tooltip that lists events, shared page-wide. */
@component({
  name: "lona-event-tooltip",
})
export class EventTooltip extends LonaWebComponent {
  static get instance(): EventTooltip {
    return tooltipInstance.get();
  }

  /** List `events`, titled and coloured as `scope` (`client.events`) shows them. */
  bind(events: EventItem[], scope: EventItemPresentation.Scope): EventTooltip {
    DomUtils.clearChildren(this.$("root"));
    for (const event of events) {
      this.$("root").appendChild(EventTooltipLine.makeWith(event, scope));
    }
    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        pointer-events: none;
        display: block;
        width: fit-content;
      }
      #root {
        margin-top: 4px;
        width: 200px;
        border-radius: 12px;
        padding: 8px 16px;
        box-shadow: var(--box-shadow);
        background: var(--bg);
        color: var(--text-sec);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root"></std-col>
  `;
}

/** The page's one event tooltip, made on first hover (`EventTooltip.instance`). */
const tooltipInstance = new LazySync(() => EventTooltip.make());

/** One event in the event tooltip. */
@component({
  name: "lona-event-tooltip-line",
})
export class EventTooltipLine extends LonaWebComponent {
  static makeWith(e: EventItem, scope: EventItemPresentation.Scope): EventTooltipLine {
    return EventTooltipLine.make().bind(e, scope);
  }

  /** Show `e`, titled and coloured as `scope` (`client.events`) shows it. */
  bind(e: EventItem, scope: EventItemPresentation.Scope): EventTooltipLine {
    this.style.setProperty("--p-tag-color", EventItemPresentation.backgroundColor(e, scope));
    this.$("title").textContent = EventItemPresentation.title(e, scope);
    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      #root {
        padding-block: 4px;
        align-items: center;
      }
      #tag-color {
        width: 4px;
        min-height: 16px;
        align-self: stretch;
        border-radius: 2px;
        background: var(--p-tag-color);
        margin-right: 8px;
        flex-shrink: 0;
      }

      #title {
        --margin-top: 2px;
        --margin-bottom: 2px;
        --line-height-ratio: 1;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-row id="root">
      <div id="tag-color"></div>
      <p id="title" class="small">Hello</p>
    </std-row>
  `;
}

/** Types that go with `EventTooltip`: `Handle`. */
export namespace EventTooltip {
  export interface Handle {
    show(
      identifier: string,
      events: EventItem[],
      position: { x: ColumnIndex.Key.Resolved.WithSubindex; y: RowLocalId },
      align: "top" | "bottom",
      options: Option<{
        offset?: Point;
        margin?: Inset;
      }>,
    ): void;
    hide(identifier: string): void;
  }
}
